/**
 * CNC Wishlist — anon-first state manager + heart toggle handler.
 *
 * v1 ships ANON ONLY (localStorage). Backend persistence comes online when
 * Supabase migration + edge function + Shopify app proxy are deployed
 * (RFC-WISHLIST-001 §3 + §6) — that's a one-line swap in fetchRemote().
 *
 * Pattern: single source of truth in window.cncWishlist (Set of product IDs).
 * All hearts on the page read from this Set on init + on state-change events.
 *
 * Klaviyo events fired on toggle: 'Wishlisted' / 'Wishlist Removed'
 * (RFC-WISHLIST-001 §5; requires Klaviyo on-site script to be loaded —
 * the cutover-prep flip will re-enable that embed).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'cnc_wishlist_v1';
  var ANON_ID_KEY = 'cnc_wishlist_anon_id_v1';
  var EVENT_CHANGED = 'cnc:wishlist:changed';
  var API_URL = 'https://dkzndqkjjzhmmcebqoib.supabase.co/functions/v1/wishlist-api';

  /* Resolve a persistent customer ID:
     - logged-in customer (window.cncCustomerId set by Liquid in theme.liquid) takes precedence
     - otherwise an anon UUID stored in localStorage; same browser keeps the same ID across sessions
     v2 hardening (post app-proxy): customer_id arrives Shopify-verified via the proxy instead. */
  function genUUID() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  function getCustomerId() {
    if (window.cncCustomerId) return { id: String(window.cncCustomerId), is_anon: false };
    try {
      var stored = localStorage.getItem(ANON_ID_KEY);
      if (stored) return { id: stored, is_anon: true };
      var fresh = 'anon-' + genUUID();
      localStorage.setItem(ANON_ID_KEY, fresh);
      return { id: fresh, is_anon: true };
    } catch (e) { return { id: 'anon-' + Date.now(), is_anon: true }; }
  }

  function apiPost(body) {
    return fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(function () { /* offline / network — local state still works */ });
  }
  function apiGet(customer_id) {
    return fetch(API_URL + '?customer_id=' + encodeURIComponent(customer_id))
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; });
  }

  function loadLocal() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) { return {}; }
  }

  function saveLocal(map) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch (e) {}
  }

  /* Public state — keyed by product_id (string) → {handle, title, url, image, price, added_at, source} */
  window.cncWishlist = window.cncWishlist || loadLocal();

  function isWishlisted(pid) {
    return !!window.cncWishlist[String(pid)];
  }

  function add(meta) {
    var pid = String(meta.productId);
    window.cncWishlist[pid] = {
      handle: meta.handle,
      title: meta.title,
      url: meta.url,
      image: meta.image,
      price: meta.price,
      added_at: new Date().toISOString(),
      source: meta.source || 'unknown',
    };
    saveLocal(window.cncWishlist);
    var c = getCustomerId();
    apiPost({ action: 'add', customer_id: c.id, is_anon: c.is_anon, product_id: pid, source: meta.source });
    dispatch(pid, 'added', meta);
    if (c.is_anon) maybeKeepNote();
  }

  function remove(pid) {
    pid = String(pid);
    var meta = window.cncWishlist[pid];
    delete window.cncWishlist[pid];
    saveLocal(window.cncWishlist);
    var c = getCustomerId();
    apiPost({ action: 'remove', customer_id: c.id, product_id: pid });
    dispatch(pid, 'removed', meta || {});
  }

  function dispatch(pid, action, meta) {
    document.dispatchEvent(new CustomEvent(EVENT_CHANGED, {
      detail: { productId: pid, action: action, meta: meta, count: Object.keys(window.cncWishlist).length },
    }));
    fireKlaviyo(action, meta);
    syncAllHearts();
  }

  function fireKlaviyo(action, meta) {
    /* Klaviyo's on-site script exposes window._learnq.push */
    if (!window._learnq || typeof window._learnq.push !== 'function') return;
    var eventName = action === 'added' ? 'Wishlisted' : 'Wishlist Removed';
    try {
      window._learnq.push(['track', eventName, {
        ProductName: meta.title || '',
        ProductID: meta.productId || '',
        Handle: meta.handle || '',
        URL: meta.url ? (location.origin + meta.url) : '',
        ImageURL: meta.image || '',
        Price: meta.price || '',
        Source: meta.source || '',
      }]);
    } catch (e) {}
  }

  function syncHeart(btn) {
    var pid = btn.getAttribute('data-cnc-wl-product-id');
    var on = isWishlisted(pid);
    btn.classList.toggle('cnc-wl--on', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
  }

  function syncAllHearts() {
    document.querySelectorAll('[data-cnc-wl-toggle]').forEach(syncHeart);
    updateHeaderCount();
  }

  function updateHeaderCount() {
    var n = Object.keys(window.cncWishlist).length;
    document.querySelectorAll('[data-cnc-wl-count]').forEach(function (el) {
      el.textContent = String(n);
      el.classList.toggle('cnc-wl-count--empty', n === 0);
    });
  }

  function onToggleClick(e) {
    var btn = e.target.closest('[data-cnc-wl-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var pid = btn.getAttribute('data-cnc-wl-product-id');
    if (!pid) return;
    if (isWishlisted(pid)) {
      remove(pid);
    } else {
      add({
        productId: pid,
        handle: btn.getAttribute('data-cnc-wl-handle'),
        title: btn.getAttribute('data-cnc-wl-title'),
        url: btn.getAttribute('data-cnc-wl-url'),
        image: btn.getAttribute('data-cnc-wl-image'),
        price: btn.getAttribute('data-cnc-wl-price'),
        source: btn.getAttribute('data-cnc-wl-source'),
      });
      /* Small pulse animation cue */
      btn.classList.add('cnc-wl--pulse');
      setTimeout(function () { btn.classList.remove('cnc-wl--pulse'); }, 600);
    }
  }

  function productUrl(handle) {
    return window.cncUrl ? window.cncUrl('products/' + handle) : '/products/' + handle;
  }

  function hydrateFromBackend() {
    var c = getCustomerId();
    /* Signed in with a guest list still on this browser: merge guest → customer FIRST, then read the list,
       so the read includes what was just merged. The guest key is dropped only once the merge succeeded —
       a failed merge keeps the list reachable for the next visit. */
    var merging = Promise.resolve(null);
    if (!c.is_anon) {
      var anonId = null;
      try { anonId = localStorage.getItem(ANON_ID_KEY); } catch (e) {}
      if (anonId) {
        merging = (apiPost({ action: 'merge', from_anon_id: anonId, to_customer_id: c.id }) || Promise.resolve(null))
          .then(function (r) { return r && r.ok ? r.json().catch(function () { return null; }) : null; })
          .then(function (d) {
            if (d && d.ok) { try { localStorage.removeItem(ANON_ID_KEY); } catch (e) {} }
            return d;
          });
      }
    }
    merging.then(function () {
      keepSaved(c);
      return apiGet(c.id);
    }).then(function (resp) {
      /* Server state into local. Items saved on another device arrive with their handle, title and image
         (wishlist-api); an id with no live product behind it has no handle and is left out. */
      var items = (resp && resp.items) || [];
      var changed = false;
      items.forEach(function (it) {
        if (!it.handle) return;
        var cur = window.cncWishlist[it.product_id];
        if (!cur) {
          window.cncWishlist[it.product_id] = {
            handle: it.handle, title: it.title || '', url: productUrl(it.handle),
            image: it.image || '', price: '',
            added_at: it.added_at, source: it.source || 'server',
          };
          changed = true;
        } else if (!cur.handle) {
          cur.handle = it.handle; cur.title = cur.title || it.title || ''; cur.image = cur.image || it.image || '';
          cur.url = productUrl(it.handle);
          changed = true;
        }
      });
      if (changed) {
        saveLocal(window.cncWishlist);
        /* not dispatch(): a sync is not a shopper action, so no Klaviyo event — just redraw */
        document.dispatchEvent(new CustomEvent(EVENT_CHANGED, {
          detail: { productId: '', action: 'synced', meta: {}, count: Object.keys(window.cncWishlist).length },
        }));
        syncAllHearts();
      }
    });
  }

  /* ── KEEP MY WISHLIST (Tracy 2026-10-13: "there's no point wishlisting something if you don't get to save
     it"). A guest's list is stored against this browser only (an anon id), so it is lost on another device
     or a cleared browser. The ask: their email → Shopify's sign-in, prefilled (login_hint) → a 6-digit code,
     no password and no newsletter → back here signed in, and hydrateFromBackend() merges the guest list into
     the account. From then on it is on every device they sign in on.
     Where: (1) a small note after a guest's FIRST heart, at most once a browser session, never again for
     30 days after its ×; (2) always, for guests with a list, at the top of the wishlist page.
     Measured in email_signup_popup_event (no schema change, the bag-save pattern): 'shown' with trigger
     wishsave_<place>_seen when the ask is on screen and wishsave_<place>_submit when they send their email;
     and wishsave_account_<place> when they come back signed in (the list merged). All three are event 'shown':
     email-signup records 'subscribed' only with an email, so the trigger name carries the step.
     The "email me my list" route and the account-vs-email-vs-both test arrive once that email exists. */
  var KEEP_ENDPOINT = 'https://dkzndqkjjzhmmcebqoib.supabase.co/functions/v1/email-signup';
  var KEEP_PENDING_KEY = 'cnc_wishsave_pending_v1';
  var KEEP_DISMISS_KEY = 'cnc_wishsave_dismissed_v1';
  var KEEP_SESSION_KEY = 'cnc_wishsave_note_shown_v1';
  var KEEP_DISMISS_DAYS = 30;

  function keepLog(event, trigger) {
    if (window.CNC_INTERNAL) return;   /* our own browsers are never counted */
    var pid = null;
    try { pid = localStorage.getItem('cnc_popup_id_v1'); } catch (e) {}
    try {
      fetch(KEEP_ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ event: event, popup_id: pid, trigger: trigger, country: (window.Shopify && window.Shopify.country) || null }),
      }).catch(function () {});
    } catch (e) {}
  }

  /* Back from sign-in: count the save once, against the place that asked. */
  function keepSaved(c) {
    if (c.is_anon) return;
    var place = null;
    try { place = localStorage.getItem(KEEP_PENDING_KEY); localStorage.removeItem(KEEP_PENDING_KEY); } catch (e) {}
    if (place) keepLog('shown', 'wishsave_account_' + place);   /* 'subscribed' needs an email at email-signup; the trigger names the save */
  }

  function keepGo(email, place) {
    try { localStorage.setItem(KEEP_PENDING_KEY, place); } catch (e) {}
    keepLog('shown', 'wishsave_' + place + '_submit');
    var login = window.cncUrl ? window.cncUrl('customer_authentication/login') : '/customer_authentication/login';
    var back = location.pathname + location.search;
    location.href = login + '?return_to=' + encodeURIComponent(back) + '&login_hint=' + encodeURIComponent(email);
  }

  function keepBlock(place) {
    var el = document.createElement('div');
    el.className = 'cnc-wishsave cnc-wishsave--' + place;
    el.innerHTML =
      '<p class="cnc-wishsave__line"></p>' +
      '<form class="cnc-wishsave__row" novalidate>' +
        '<input type="email" class="cnc-wishsave__input" placeholder="Your email" autocomplete="email" aria-label="Your email" required>' +
        '<button type="submit" class="cnc-wishsave__btn">KEEP MY WISHLIST</button>' +
      '</form>' +
      '<p class="cnc-wishsave__fine">You\'ll get a code to sign in. No password, no newsletter.</p>' +
      '<p class="cnc-wishsave__msg" role="status" hidden></p>';
    el.querySelector('.cnc-wishsave__line').textContent = place === 'page'
      ? 'Right now your wishlist only lives on this device. Pop your email in and I\'ll keep it for you, on any device.'
      : 'Saved! Pop your email in and I\'ll keep your wishlist for you, on any device.';
    el.querySelector('form').addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (el.querySelector('input').value || '').trim();
      var msg = el.querySelector('.cnc-wishsave__msg');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { msg.textContent = 'Please enter a valid email.'; msg.hidden = false; return; }
      keepGo(email, place);
    });
    return el;
  }

  function keepDismissedRecently() {
    try {
      var t = parseInt(localStorage.getItem(KEEP_DISMISS_KEY) || '0', 10);
      return t && (Date.now() - t) < KEEP_DISMISS_DAYS * 864e5;
    } catch (e) { return false; }
  }

  function maybeKeepNote() {
    if (!getCustomerId().is_anon || document.querySelector('[data-cnc-wishsave-page]')) return;  /* the page has its own */
    if (keepDismissedRecently()) return;
    try { if (sessionStorage.getItem(KEEP_SESSION_KEY)) return; sessionStorage.setItem(KEEP_SESSION_KEY, '1'); } catch (e) { return; }
    var sheet = document.createElement('div');
    sheet.className = 'cnc-wishsave-note';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-label', 'Keep your wishlist');
    var x = document.createElement('button');
    x.type = 'button'; x.className = 'cnc-wishsave-note__x'; x.setAttribute('aria-label', 'Close'); x.innerHTML = '&times;';
    x.addEventListener('click', function () {
      try { localStorage.setItem(KEEP_DISMISS_KEY, String(Date.now())); } catch (e) {}
      sheet.remove();
    });
    sheet.appendChild(x);
    sheet.appendChild(keepBlock('note'));
    document.body.appendChild(sheet);
    keepLog('shown', 'wishsave_note_seen');
  }

  function mountPageKeep() {
    var box = document.querySelector('[data-cnc-wishsave-page]');
    if (!box || !getCustomerId().is_anon) return;
    var show = function () {
      var has = Object.keys(window.cncWishlist).some(function (k) { return window.cncWishlist[k] && window.cncWishlist[k].handle; });
      if (has && !box.childNodes.length) {
        box.appendChild(keepBlock('page'));
        try { if (!sessionStorage.getItem('cnc_wishsave_page_seen_v1')) { sessionStorage.setItem('cnc_wishsave_page_seen_v1', '1'); keepLog('shown', 'wishsave_page_seen'); } } catch (e) {}
      }
      box.hidden = !has;
    };
    show();
    document.addEventListener(EVENT_CHANGED, show);
  }

  function init() {
    document.addEventListener('click', onToggleClick, true);
    mountPageKeep();
    syncAllHearts();
    hydrateFromBackend();
    /* Re-sync after Dawn re-renders cards (infinite scroll, facet change).
       CRITICAL: only fire when NEW heart elements are inserted — never on
       attribute or text changes (otherwise syncAllHearts modifies DOM and
       re-triggers this observer = infinite loop = page never finishes load). */
    var pending = false;
    var mo = new MutationObserver(function (mutations) {
      if (pending) return;
      var hasNewHeart = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-cnc-wl-toggle]')) { hasNewHeart = true; break; }
          if (n.querySelector && n.querySelector('[data-cnc-wl-toggle]')) { hasNewHeart = true; break; }
        }
        if (hasNewHeart) break;
      }
      if (!hasNewHeart) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        syncAllHearts();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Public API for /pages/wishlist + header */
  window.cncWishlistAPI = {
    isWishlisted: isWishlisted,
    add: add,
    remove: remove,
    list: function () {
      return Object.keys(window.cncWishlist).map(function (pid) {
        var v = window.cncWishlist[pid];
        return Object.assign({ productId: pid }, v);
      }).sort(function (a, b) { return (b.added_at || '').localeCompare(a.added_at || ''); });
    },
    onChange: function (cb) { document.addEventListener(EVENT_CHANGED, function (e) { cb(e.detail); }); },
  };
})();
